// /admin — the home, v2 « cockpit » (owner-approved 2026-10-22; v1 2026-10-21).
// « I need to know all the infos in a split of a second … if anybody sees my
// dashboard, they need to know how to react and what to do. »
//
// One laptop screen (1440×900, no scroll), top → bottom:
//   header   « Accueil », the date, « mis à jour il y a N min », and the period
//            buttons Aujourd'hui / 7 jours / 30 jours / Mois (the DateRangePicker
//            stays on the other pages);
//   1. VERDICT — exactly 3 tiles, red / amber / green, worst first, each with
//      what to do (HomeVerdictRow; decided by the worker, lib/homeVerdict.ts);
//   2. KPIs — 6 cards with change vs the previous equal period and a 14-day
//      sparkline (HomeKpiRow; worker lib/homeKpis.ts + the ONE profit formula,
//      lib/profit.ts);
//   3. « À faire maintenant » (HomeTodoCard, never bound to the period) beside
//      « Ventes vs pub » (HomeSalesChart) and « Caisse du jour » (HomeCashCard);
//   4. « Production » — 6 clickable steps (HomePipelineCard).
// Phone (390): verdict → to-do → KPIs (2 columns) → chart → caisse → production.
// Everything else that lived here is on Rapports (/admin/rapports).
//
// Kept: the small Install-app / Push strip under the blocks; for staff agents
// their own cards (earnings, queue, own stats). An agent never receives company
// money (finance.view), so an agent's home is the to-do list and their cards.
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdminLayout } from '@/components/AdminLayout';
import { InstallAppCard } from '@/components/admin/InstallAppCard';
import { PushNotificationsCard } from '@/components/admin/PushNotificationsCard';
import { AgentEarningsCard } from '@/components/admin/AgentEarningsCard';
import { OrderQueueCard } from '@/components/admin/OrderQueueCard';
import { AgentOwnStats } from '@/components/admin/AgentOwnStats';
import { presetRange, DateRangePicker, type DateRange } from '@/components/admin/DateRangePicker';
import { HomeVerdictRow } from '@/components/admin/home/HomeVerdictRow';
import { HomeKpiRow } from '@/components/admin/home/HomeKpiRow';
import { HomeTodoCard } from '@/components/admin/home/HomeTodoCard';
import { HomeSalesChart } from '@/components/admin/home/HomeSalesChart';
import { HomeCashCard } from '@/components/admin/home/HomeCashCard';
import { HomePipelineCard } from '@/components/admin/home/HomePipelineCard';
import { ProfitCalculator } from '@/components/admin/home/ProfitCalculator';
import { Modal } from '@/components/ui/Modal';
import { dashboardApi } from '@/lib/dashboardApi';
import { HOME_PERIODS, updatedAgo, type HomePeriod } from '@/lib/homeDashboard';
import { useT } from '@/lib/i18n';
import { usePermissions } from '@/lib/permissionsStore';
import { useAuthStore } from '@/store/useAuthStore';

type TFn = ReturnType<typeof useT>['t'];

/** Re-render every 30 s so « il y a N min » stays true. */
function useNow(everyMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(id);
  }, [everyMs]);
  return now;
}

function subtitle(lang: string, t: TFn, updatedAt: number | null, now: number): string {
  const day = new Date(now).toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  if (!updatedAt) return day;
  const ago = updatedAgo(updatedAt, now);
  const text = ago.kind === 'now' ? t("mis à jour à l'instant")
    : ago.kind === 'min' ? t('mis à jour il y a {n} min', { n: ago.n }) : t('mis à jour il y a {n} h', { n: ago.n });
  return `${day} · ${text}`;
}

function PeriodButtons({ value, onChange, custom, onCustom }: {
  value: HomePeriod; onChange: (p: HomePeriod) => void; custom: DateRange; onCustom: (r: DateRange) => void;
}) {
  const { t } = useT();
  return (
    <div className="flex flex-wrap md:flex-nowrap items-center gap-2 w-full md:w-auto">
    <div role="group" aria-label={t('Période')}
      className="flex w-full md:w-auto bg-white border border-[#ECE5D6] dark:border-adm-line rounded-[10px] p-[3px] gap-0.5">
      {HOME_PERIODS.map((p) => (
        <button key={p.key} type="button" aria-pressed={value === p.key} onClick={() => onChange(p.key)}
          className={`flex-1 md:flex-none px-[11px] py-[5px] rounded-[7px] text-[12.5px] font-medium whitespace-nowrap transition-colors ${
            value === p.key ? 'bg-[#2B2116] text-[#FBF7EC] dark:bg-adm-gold dark:text-[#2B2116]' : 'text-adm-muted hover:text-adm-ink'}`}>
          {t(p.label)}
        </button>
      ))}
    </div>
    {/* Any dates, like before: yesterday, 14 days, last month, a custom range, all. */}
    <DateRangePicker value={value === 'custom' ? custom : presetRange(value)} onChange={onCustom} align="right" />
    </div>
  );
}

export default function AdminDashboardPage() {
  const { t, lang } = useT();
  const { can } = usePermissions();
  const profile = useAuthStore((st) => st.profile);
  const isFullAdmin = profile?.role === 'admin' || can('*');
  // `can()` is false until permissions load, so agent cards never flash for an admin.
  const isAgent = !isFullAdmin && can('orders.edit_status');
  const canFinance = can('finance.view');
  const canPush = isFullAdmin || can('whatsapp.manage');
  const [period, setPeriod] = useState<HomePeriod>('7d');
  const [custom, setCustom] = useState<DateRange>({});
  // « Profit net » opens the calculator (owner, 2026-10-22).
  const [calcOpen, setCalcOpen] = useState(false);
  const dates = useMemo(() => (period === 'custom' ? custom : presetRange(period)), [period, custom]);
  const days = dates.from ? Math.max(1, Math.round(((dates.to ? Date.parse(dates.to) : Date.now()) - Date.parse(dates.from)) / 86_400_000)) : null;
  const now = useNow();

  const overview = useQuery({
    queryKey: ['home-overview', period, dates.from ?? null, dates.to ?? null] as const,
    queryFn: () => dashboardApi.overview({ ...dates, ...(period === 'custom' ? {} : { preset: period }) }),
    enabled: canFinance,
    staleTime: 60_000,
    refetchInterval: 300_000,
  });
  const d = overview.data;
  const updatedAt = canFinance && overview.dataUpdatedAt ? overview.dataUpdatedAt : null;

  return (
    <AdminLayout
      title={t('Accueil')}
      subtitle={subtitle(lang, t, updatedAt, now)}
      actions={canFinance ? <PeriodButtons value={period} onChange={setPeriod} custom={custom} onCustom={(r) => { setCustom(r); setPeriod('custom'); }} /> : undefined}
    >
      {isAgent && <AgentEarningsCard range={dates} />}
      {isAgent && <OrderQueueCard />}

      {/* Phone: DOM order (verdict → to-do → KPIs → chart → caisse → production).
          Laptop: grid placement (verdict, KPIs, to-do | chart + caisse, production). */}
      <div className={`grid grid-cols-1 gap-2.5 lg:gap-3 ${canFinance ? 'lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]' : ''}`}>
        {canFinance && (
          <div className="lg:col-span-2 lg:row-start-1 min-w-0">
            {overview.error
              ? <p className="text-sm text-status-stuck">{t('Chiffres indisponibles — aucun chiffre plutôt qu’un chiffre faux.')}</p>
              : <HomeVerdictRow tiles={d?.verdict} isLoading={overview.isLoading} />}
          </div>
        )}
        <HomeTodoCard className={canFinance ? 'lg:col-start-1 lg:row-start-3 lg:row-span-2' : ''} />
        {canFinance && (
          <div className="lg:col-span-2 lg:row-start-2 min-w-0">
            <HomeKpiRow data={d} isLoading={overview.isLoading} period={period} onProfitClick={() => setCalcOpen(true)} />
          </div>
        )}
        {canFinance && <HomeSalesChart series={d?.series} isLoading={overview.isLoading} className="lg:col-start-2 lg:row-start-3" />}
        {canFinance && <HomeCashCard data={d} isLoading={overview.isLoading} days={days} className="lg:col-start-2 lg:row-start-4 lg:self-start" />}
        {canFinance && (
          <div className="lg:col-span-2 lg:row-start-5 min-w-0">
            <HomePipelineCard steps={d?.pipeline.steps} isLoading={overview.isLoading} period={period} />
          </div>
        )}
      </div>

      {isAgent && <div className="mt-6"><AgentOwnStats /></div>}

      {canFinance && (
        <Modal isOpen={calcOpen} onClose={() => setCalcOpen(false)} title={t('Calculateur de profit')} size="xl">
          <div className="p-4 sm:p-5 overflow-y-auto"><ProfitCalculator initialPeriod={period} compact /></div>
        </Modal>
      )}

      {/* « Install the app » / « Turn on notifications » — each hides itself
          when it has nothing to offer; `empty:hidden` drops the strip. Below
          the blocks: the laptop view keeps the cockpit on one screen. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 empty:hidden">
        <InstallAppCard />
        {canPush && <PushNotificationsCard hideWhenOn />}
      </div>
    </AdminLayout>
  );
}
